import {generatePath, useNavigate} from 'react-router-dom'
import {routes} from '@/utilities/constants/routes'
import {Button} from '@/components/ui/button/Button'
import {FolderIcon, PlusIcon} from '@/components/ui/icon/Icon'
import {Spinner} from '@/components/ui/spinner/Spinner'
import {useTranslation} from '@/translations/i18n'
import {QUERY_KEYS, queryClient} from '@/queryClient'
import {useCreateMedicalRecord} from '@/features/medical-records/queries/useCreateMedicalRecord'
import {CreateMedicalReportSearchParams} from '@/features/medical-records/types'
import {useCallback} from 'react'
import {generateCreateMedicalReportPath} from '@/features/medical-reports/utils'
import {PatientExtended} from '@/features/patients/types'

export interface MedicalRecordPatientData {
    id: string
    project: PatientExtended['project']
    residenceAslDistrict: PatientExtended['residenceAslDistrict']
    neuromuscularDiagnosis: PatientExtended['neuromuscularDiagnosis']
    fullName: PatientExtended['fullName']
    medicalRecord: PatientExtended['medicalRecord']
    treatmentRequestEligibility: PatientExtended['treatmentRequestEligibility']
}

interface CreateMedicalRecordButtonProps {
    /**
     * The patient data required for creating medical record
     */
    patient: MedicalRecordPatientData
    /**
     * If true, will navigate to create report page after successfully creating medical record
     */
    navigateToCreateReport?: boolean
    /**
     * If true, will hide the icon
     */
    hideIcon?: boolean
    /**
     * Search params to pass to the create report page
     */
    searchParams?: CreateMedicalReportSearchParams
    /**
     * Button variant from design system
     */
    variant?: 'primary' | 'secondary' | 'primaryNeutral' | 'tertiary'
    /**
     * Button size from design system
     */
    size?: 'sm' | 'md'
    /**
     * Custom callback executed after successful creation
     */
    onSuccess?: () => void
    /**
     * Custom callback executed on error
     */
    onError?: () => void
    /**
     * Children to render inside button (overrides default text)
     */
    children?: React.ReactNode
}

export const CreateMedicalRecordButton = ({
    patient,
    navigateToCreateReport = false,
    searchParams,
    variant = 'secondary',
    size = 'md',
    hideIcon = false,
    onSuccess,
    onError,
    children
}: CreateMedicalRecordButtonProps) => {
    const {t} = useTranslation()
    const navigate = useNavigate()
    const canCreateReport = !!patient.project

    const createMedicalRecordMutation = useCreateMedicalRecord({
        onSuccess: data => {
            void queryClient.cancelQueries({queryKey: [QUERY_KEYS.PATIENTS, patient.id]})
            void queryClient.setQueriesData(
                {queryKey: [QUERY_KEYS.PATIENTS, patient.id]},
                {
                    ...patient,
                    medicalRecord: data
                }
            )

            if (navigateToCreateReport && canCreateReport) {
                if (searchParams?.projectId && searchParams?.medicalReportType) {
                    const path = generateCreateMedicalReportPath(
                        patient.id,
                        searchParams.projectId,
                        searchParams.medicalReportType
                    )
                    if (path) {
                        navigate(path)
                    }
                } else {
                    const path = generatePath(routes.CREATE_MEDICAL_REPORT.path, {id: patient.id})
                    navigate(path)
                }
            }

            onSuccess?.()
        },
        onError: () => {
            onError?.()
        }
    })

    const handleCreateMedicalRecord = useCallback(() => {
        createMedicalRecordMutation.mutate({payload: {patientId: patient.id}})
    }, [createMedicalRecordMutation, patient.id])

    // Determine which icon to show based on variant
    const icon = variant === 'primaryNeutral' ? <PlusIcon /> : <FolderIcon />

    return (
        <Button
            variant={variant}
            disabled={createMedicalRecordMutation.isPending}
            onClick={handleCreateMedicalRecord}
            size={size}
        >
            {!hideIcon && icon}
            {children || t('medical_records:create_medical_record')}
            {createMedicalRecordMutation.isPending && <Spinner />}
        </Button>
    )
}
